/**
 * Button Atom
 *
 * A fundamental interactive element for triggering actions.
 * Supports multiple variants, sizes, and loading states.
 */

import { cva } from 'class-variance-authority';
import { RefreshCw } from 'lucide-react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'default'
    | 'text'
    | 'outline'
    | 'red'
    | 'white'
    | 'primary'
    | 'outline-primary'
    | 'outline-green'
    | 'green'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'input';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  isLoading?: boolean;
}

const buttonClass = 'flex justify-center items-center gap-2 font-light text-white relative px-4 py-2 w-fit';

const customButtonVariants = {
  primary: 'bg-primary hover:enabled:bg-primary-600 disabled:bg-primary-200',
  default: 'bg-foreground text-background hover:enabled:bg-primary hover:enabled:text-white disabled:opacity-50',
  text: 'text-muted-foreground hover:enabled:bg-accent disabled:text-muted-foreground/50',
  outline:
    'text-foreground border border-foreground hover:enabled:bg-primary hover:enabled:border-primary hover:enabled:text-white disabled:text-muted-foreground/50 disabled:border-gray2',
  'outline-green':
    'text-foreground border border-foreground hover:enabled:bg-emerald-600 hover:enabled:border-bg-emerald-600 hover:enabled:text-white disabled:text-muted-foreground/50 disabled:border-gray2',
  red: 'bg-primary-600 hover:enabled:bg-red-800 disabled:bg-primary-200',
  white: 'bg-card text-card-foreground hover:enabled:bg-card/80 disabled:bg-muted',
  'outline-primary':
    'text-muted-foreground border border-border hover:bg-primary hover:border-primary hover:text-white dark:border-primary dark:text-primary dark:hover:bg-primary transition duration-200',
  green: 'bg-emerald-600 hover:enabled:bg-emerald-700 disabled:bg-emerald-200',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-secondary/80 dark:bg-zinc-800 dark:text-zinc-50 dark:hover:bg-zinc-800/80',
  ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-zinc-800 dark:hover:text-zinc-50',
  link: 'text-foreground underline-offset-4 hover:text-primary dark:text-zinc-50 p-0',
  input: 'bg-transparent hover:bg-transparent text-foreground',
};

export const Button: React.FC<ButtonProps> = ({ variant = 'default', isLoading, disabled, ...props }) => (
  <button
    {...props}
    disabled={isLoading || disabled}
    className={twMerge(buttonClass, customButtonVariants[variant], props.className)}>
    {isLoading ? <RefreshCw className='h-5 w-5 animate-spin' /> : props.children}
  </button>
);

export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-light ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-zinc-950 dark:focus-visible:ring-zinc-300',
  {
    variants: {
      variant: {
        default:
          'bg-zinc-900 hover:bg-primary hover:text-white text-zinc-50 hover:bg-zinc-900/90 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-50/90',
        destructive:
          'bg-red-500 text-zinc-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-zinc-50 dark:hover:bg-red-900/90',
        outline:
          'border border-zinc-200 bg-white hover:bg-primary hover:text-white hover:border-primary dark:border-zinc-800 dark:bg-zinc-950 dark:hover:bg-zinc-800 dark:hover:text-zinc-50',
        secondary:
          'bg-zinc-100 text-zinc-900 hover:bg-zinc-100/80 dark:bg-zinc-800 dark:text-zinc-50 dark:hover:bg-zinc-800/80',
        ghost: 'hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-50',
        link: 'text-zinc-900 underline-offset-4 hover:text-primary dark:text-zinc-50',
        'outline-primary':
          'text-zinc-700 border border-zinc-300 hover:bg-primary hover:border-primary hover:text-white dark:border-primary dark:text-primary dark:hover:bg-primary transition duration-200',
        input: 'bg-transparent hover:bg-transparent',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9  px-3',
        lg: 'h-11  px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);
